<template>
    <ul class="flex flex-col">
        <li v-for="review in selfReviews" :key="review.id" class="row-item">
            <PickerRow
                :review="review"
                :current-conversation-hashid="currentConversationHashid"
                :is-busy="isBusy"
                :start-disabled="startDisabled"
                @row-clicked="emit('row-clicked', $event)" />
        </li>
        <li v-if="hasTeamRow" class="row-item">
            <!-- Opens a list rather than starting a task, so it reads like a moment row: the whole row is the control.
                 With nobody added there is no list to open, so it only reports that. -->
            <button
                type="button"
                class="group flex min-h-14 w-full items-center gap-3 py-3 ps-2.5 text-start disabled:cursor-default"
                :disabled="!teamReviews.length"
                data-testid="picker-team-row"
                @click="emit('open-team')">
                <span class="flex w-4 shrink-0 justify-center"><ProgressIcon :status="teamProgress" /></span>
                <span class="grow min-w-0 body-small text-ink truncate">{{ teamLabel }}</span>
                <span class="shrink-0 scaffold-details-small text-ink-muted">{{ teamStatus }}</span>
                <i v-if="teamReviews.length" class="bi bi-chevron-right shrink-0 text-xs/none text-ink-muted transition-colors group-hover:text-ink" aria-hidden="true"></i>
            </button>
        </li>
    </ul>
</template>

<script setup lang="ts">
import { isReviewCompleted } from "~vue/Moments/composables/useReviewGroupStatus";
import type { DecoratedPerformanceReview } from "~vue/types/performance_management";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import PickerRow from "../ChatPerformancePickerRow.vue";
import ProgressIcon, { type ProgressStatus } from "./ProgressIcon.vue";

interface Props {
    selfReviews: DecoratedPerformanceReview[];
    teamReviews: DecoratedPerformanceReview[];
    teamLabel: string;
    /** Keeps the team row when nobody has been added yet, because the person phase can add them. */
    showEmptyTeamRow?: boolean;
    currentConversationHashid?: string;
    isBusy?: boolean;
    startDisabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    showEmptyTeamRow: false,
    currentConversationHashid: "",
    isBusy: false,
    startDisabled: false,
});

const emit = defineEmits<{ "row-clicked": [review: DecoratedPerformanceReview]; "open-team": [] }>();
const { t } = useI18n();

const hasTeamRow = computed(() => !!props.teamReviews.length || props.showEmptyTeamRow);

const completedReviewCount = computed(() => props.teamReviews.filter(isReviewCompleted).length);
const teamStatus = computed(() =>
    props.teamReviews.length ? t("chat.pmPicker.teamProgress", { completed: completedReviewCount.value, total: props.teamReviews.length }) : t("chat.pmPicker.noneAdded"),
);

const teamProgress = computed<ProgressStatus>(() => {
    if (props.teamReviews.length && completedReviewCount.value === props.teamReviews.length) {
        return "done";
    }
    return completedReviewCount.value || props.teamReviews.some((review) => review.conversation) ? "in-progress" : "not-started";
});
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

@layer components {
    .row-item {
        @apply border-b border-border;
    }

    .row-item:last-child {
        @apply border-b-0;
    }
}
</style>
